import {
  ActivityCategory,
  ActivityDifficulty,
  ActivitySortField,
  ActivitySortOrder,
  ActivityUsageFilter,
  GetActivitiesQueryVariables,
} from "@/graphql/graphql";

export type ActivityUsageFilterOption = {
  label: string;
  value?: ActivityUsageFilter;
};

/** Single-select usage filter labels (empty value = not filtered). */
export const ACTIVITY_USAGE_FILTER_OPTIONS: ActivityUsageFilterOption[] = [
  { label: "Not filtered" },
  { label: "Past and Future", value: ActivityUsageFilter.AnyUse },
  { label: "Future", value: ActivityUsageFilter.FutureUse },
  { label: "Past", value: ActivityUsageFilter.PastUse },
  { label: "Not used", value: ActivityUsageFilter.NoUse },
];

export const activityUsageFilterOptionForValue = (
  usage?: ActivityUsageFilter
): ActivityUsageFilterOption =>
  ACTIVITY_USAGE_FILTER_OPTIONS.find((option) => option.value === usage) ??
  ACTIVITY_USAGE_FILTER_OPTIONS[0];

export interface ActivitiesQueryState {
  page: number;
  pageSize: number;
  titleSearch?: string;
  contentSearch?: string;
  categories?: ActivityCategory[];
  typeKeys?: string[];
  difficulties?: ActivityDifficulty[];
  usage?: ActivityUsageFilter;
  sortField?: ActivitySortField;
  sortDirection?: ActivitySortOrder;
}

export const initialActivitiesQueryState = (): ActivitiesQueryState => ({
  page: 0,
  pageSize: 20,
});

export const buildActivitiesVariables = (
  {
    page,
    pageSize,
    titleSearch,
    contentSearch,
    categories,
    typeKeys,
    difficulties,
    usage,
    sortField,
    sortDirection,
  }: ActivitiesQueryState,
  accountId?: number
): GetActivitiesQueryVariables => ({
  accountId,
  skip: page * pageSize,
  take: pageSize,
  sort: sortField &&
    sortDirection && {
      fieldName: sortField,
      direction: sortDirection,
    },
  filters:
    titleSearch || contentSearch || categories || difficulties || typeKeys || usage
      ? {
          titleSearch: titleSearch,
          contentSearch: contentSearch,
          categories: categories,
          difficulties: difficulties,
          typeKeys: typeKeys,
          usage: usage,
        }
      : undefined,
});

export type ActivitiesQueryAction =
  | { type: "SET_PAGE"; page: number }
  | { type: "SET_PAGE_SIZE"; pageSize: number }
  | { type: "SET_TITLE_SEARCH"; titleSearch: string }
  | { type: "SET_CONTENT_SEARCH"; contentSearch: string }
  | { type: "SET_CATEGORIES"; categories: ActivityCategory[] }
  | { type: "SET_TYPE_KEYS"; typeKeys: string[] }
  | { type: "SET_DIFFICULTIES"; difficulties: ActivityDifficulty[] }
  | { type: "SET_USAGE"; usage?: ActivityUsageFilter }
  | { type: "TOGGLE_SORT"; field: ActivitySortField }
  | { type: "CLEAR_FILTERS" }
  | { type: "RESET" };

export const activitiesQueryReducer = (
  state: ActivitiesQueryState,
  action: ActivitiesQueryAction
): ActivitiesQueryState => {
  switch (action.type) {
    case "SET_PAGE":
      return { ...state, page: action.page };
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.pageSize, page: 0 };
    case "SET_TITLE_SEARCH":
      return { ...state, titleSearch: action.titleSearch, page: 0 };
    case "SET_CONTENT_SEARCH":
      return { ...state, contentSearch: action.contentSearch, page: 0 };
    case "SET_CATEGORIES":
      return { ...state, categories: action.categories, page: 0 };
    case "SET_TYPE_KEYS":
      return { ...state, typeKeys: action.typeKeys, page: 0 };
    case "SET_DIFFICULTIES":
      return { ...state, difficulties: action.difficulties, page: 0 };
    case "SET_USAGE":
      return { ...state, usage: action.usage, page: 0 };
    case "TOGGLE_SORT": {
      // Toggles between ascending, descending and none.
      let sortDirection: ActivitiesQueryState["sortDirection"];

      if (state.sortField !== action.field || !state.sortDirection) {
        sortDirection = ActivitySortOrder.Asc;
      } else if (state.sortDirection === ActivitySortOrder.Asc) {
        sortDirection = ActivitySortOrder.Desc;
      }

      return { ...state, sortField: sortDirection && action.field, sortDirection, page: 0 };
    }
    case "CLEAR_FILTERS":
      return {
        ...initialActivitiesQueryState(),
        pageSize: state.pageSize,
        sortField: state.sortField,
        sortDirection: state.sortDirection,
      };
    case "RESET":
      return { ...initialActivitiesQueryState(), pageSize: state.pageSize };
    default:
      return state;
  }
};
